import React from 'react';
import { useInput, useNotify } from 'react-admin';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { IMAGE_ACCEPT, IMAGE_MAX_SIZE, textNotification } from '../common';
import { FieldLabel, HelpText } from './CharCounter';

const ACCEPTED_TYPES = IMAGE_ACCEPT.split(',');

/**
 * Selector de imagen del prototipo: «Subir imagen», el nombre del fichero y «Quitar».
 *
 * Guarda en `image` lo mismo que guardaba el `ImageInput` de react-admin (`{ rawFile, src, title }`
 * con un fichero nuevo, `{ src, title }` con la imagen guardada, `null` sin imagen), que es lo que
 * `buildNewsPostPayload` traduce a `imageBase64`. El tipo y los 2 MB se comprueban al elegir. Es
 * obligatoria: `validateNewsPost` deja el error en `image` y aquí se pinta bajo el recuadro.
 */
const ImagePicker = ({ source = 'image' }) => {
    const notify = useNotify();
    const { id, field, fieldState, formState } = useInput({ source, defaultValue: null });
    const image = field.value;
    // Obligatoria: el error sale al intentar guardar o después de quitarla, no antes de tocar nada.
    const { error } = fieldState;
    const errorText = (fieldState.isTouched || formState.isSubmitted) && error
        ? (typeof error.message === 'string' ? error.message : error.message?.message)
        : null;

    const handleChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type) || file.size > IMAGE_MAX_SIZE) {
            notify(...textNotification('La imagen debe ser PNG, JPG o WEBP y pesar como máximo 2 MB.', { type: 'warning' }));
            return;
        }
        field.onChange({ rawFile: file, src: URL.createObjectURL(file), title: file.name });
    };

    return (
        <Box>
            <FieldLabel htmlFor={id} label="Imagen *" />
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.75,
                    border: `1px dashed ${errorText ? theme.palette.error.main : theme.palette.text.disabled}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.02),
                })}
            >
                <Button
                    component="label"
                    htmlFor={id}
                    startIcon={<ImageOutlinedIcon />}
                    sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.1), minHeight: 44, px: 1.75 })}
                >
                    Subir imagen
                </Button>
                <input
                    id={id}
                    type="file"
                    accept={IMAGE_ACCEPT}
                    onChange={handleChange}
                    data-testid="news-image-input"
                    style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
                />
                <Typography
                    color="text.secondary"
                    sx={{ fontSize: '13px', flex: '1 1 160px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {image ? image.title : 'Ninguna imagen seleccionada'}
                </Typography>
                {image && (
                    <Button
                        color="error"
                        onClick={() => {
                            field.onChange(null);
                            field.onBlur();
                        }}
                        sx={{ minHeight: 44 }}
                    >
                        Quitar
                    </Button>
                )}
            </Box>
            {errorText && (
                <HelpText color="error" role="alert">
                    {errorText}
                </HelpText>
            )}
            <HelpText>PNG, JPG o WEBP. Formato horizontal recomendado (16:9).</HelpText>
        </Box>
    );
};

export default ImagePicker;
